import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  IndianRupee, Building2, AlertTriangle, TrendingUp, TrendingDown, 
  Edit2, Save, X, ChevronDown, ChevronUp, RefreshCw, CheckCircle, 
  XCircle, ArrowUpDown, Lightbulb, Shield, AlertCircle, Target,
  Percent, Wallet, CreditCard, PiggyBank, ArrowRight, Receipt, Lock, Zap
} from 'lucide-react';
import { api } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import LoanPayments from './LoanPayments';
import PledgeFlow from './PledgeFlow';
import BajajUpdate from './BajajUpdate';

const DebtManagement = ({ user, setUser }) => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [debtSummary, setDebtSummary] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [editingLAS, setEditingLAS] = useState(null);
  const [editingMTF, setEditingMTF] = useState(null);
  
  const [lasFilterBank, setLasFilterBank] = useState('all');
  const [lasFilterMember, setLasFilterMember] = useState('all');
  const [lasSortField, setLasSortField] = useState('member_name');
  const [lasSortOrder, setLasSortOrder] = useState('asc');
  
  const [expandedSections, setExpandedSections] = useState({
    insights: true,
    lasByBank: true,
    mtfByBroker: true,
    reconciliation: true
  });

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['dashboard', 'las', 'mtf', 'payments', 'pledge-flow', 'daily-update'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, reconRes] = await Promise.all([
        api.get('/debt/summary'),
        api.get('/debt/reconciliation')
      ]);
      setDebtSummary(summaryRes.data);
      setReconciliation(reconRes.data);
    } catch (error) {
      console.error('Error fetching debt data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const [lasRecords, setLasRecords] = useState([]);
  
  const fetchLASRecords = useCallback(async () => {
    try {
      const response = await api.get('/debt/las-accounts');
      setLasRecords(response.data);
    } catch (error) {
      console.error('Error fetching LAS records:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchLASRecords();
  }, [fetchData, fetchLASRecords]);

  // Generate actionable insights
  const insights = useMemo(() => {
    if (!debtSummary) return [];
    const result = [];
    
    // High LTV Warning
    const avgLTV = debtSummary?.bajaj_loans?.accounts?.reduce((sum, acc) => sum + (acc.ltv_percent || 0), 0) / 
                   (debtSummary?.bajaj_loans?.accounts?.length || 1);
    if (avgLTV > 45) {
      result.push({
        type: 'warning',
        priority: 'high',
        icon: AlertCircle,
        title: 'High Loan-to-Value Ratio',
        message: `Average LTV is ${avgLTV.toFixed(1)}%. Consider reducing loans or adding more collateral to stay below 50%.`,
        action: 'Review LAS accounts',
        actionTab: 'las'
      });
    }
    
    // MTF Safety Cover Warning
    const lowSafetyAccounts = debtSummary?.mtf_summary?.accounts?.filter(acc => acc.safety_cover_percent < 15) || [];
    if (lowSafetyAccounts.length > 0) {
      result.push({
        type: 'critical',
        priority: 'high',
        icon: Shield,
        title: 'MTF Safety Cover Alert',
        message: `${lowSafetyAccounts.length} account(s) have safety cover below 15%. Risk of margin call.`,
        action: 'Check MTF accounts',
        actionTab: 'mtf'
      });
    }
    
    // Over-utilized Bajaj accounts
    const overUtilized = debtSummary?.bajaj_loans?.accounts?.filter(acc => acc.available < 0) || [];
    if (overUtilized.length > 0) {
      const totalOverUtilized = overUtilized.reduce((sum, acc) => sum + Math.abs(acc.available), 0);
      result.push({
        type: 'critical',
        priority: 'high',
        icon: CreditCard,
        title: 'Loan Over-Utilization',
        message: `${overUtilized.length} account(s) exceeded limit by ₹${(totalOverUtilized/100000).toFixed(2)}L. Immediate action required.`,
        action: 'Review limits',
        actionTab: 'las'
      });
    }
    
    // Interest Optimization Suggestion
    if (debtSummary?.total_debt > 10000000) {
      result.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Lightbulb,
        title: 'Interest Optimization',
        message: 'With total debt over ₹1Cr, consider consolidating loans with lower-rate banks or negotiating better terms.',
        action: 'Compare rates',
        actionTab: 'dashboard'
      });
    }
    
    // Unutilized Credit
    const totalAvailable = debtSummary?.bajaj_loans?.accounts?.reduce((sum, acc) => sum + Math.max(0, acc.available || 0), 0) || 0;
    if (totalAvailable > 5000000) {
      result.push({
        type: 'info',
        priority: 'low',
        icon: PiggyBank,
        title: 'Unutilized Credit Available',
        message: `₹${(totalAvailable/10000000).toFixed(2)}Cr available credit across accounts. Consider investing if opportunities arise.`,
        action: 'View details',
        actionTab: 'las'
      });
    }
    
    // Reconciliation discrepancy
    if (reconciliation?.accounts_with_discrepancy > 0) {
      result.push({
        type: 'warning',
        priority: 'medium',
        icon: Target,
        title: 'Data Discrepancy Found',
        message: `${reconciliation.accounts_with_discrepancy} account(s) have data mismatch with bank records. Review before next bank meeting.`,
        action: 'Reconcile now',
        actionTab: 'dashboard'
      });
    }
    
    return result.sort((a, b) => {
      const priority = { high: 0, medium: 1, low: 2 };
      return priority[a.priority] - priority[b.priority];
    });
  }, [debtSummary, reconciliation]);

  const uniqueBanks = useMemo(() => {
    const banks = [...new Set(lasRecords.map(r => r.bank_name).filter(Boolean))];
    return banks.sort();
  }, [lasRecords]);

  const uniqueMembers = useMemo(() => {
    const members = [...new Set(lasRecords.map(r => r.member_name).filter(Boolean))];
    return members.sort();
  }, [lasRecords]);

  const filteredSortedLASRecords = useMemo(() => {
    let filtered = [...lasRecords];
    
    if (lasFilterBank !== 'all') {
      filtered = filtered.filter(r => r.bank_name === lasFilterBank);
    }
    if (lasFilterMember !== 'all') {
      filtered = filtered.filter(r => r.member_name === lasFilterMember);
    }
    
    filtered.sort((a, b) => {
      let aVal = a[lasSortField];
      let bVal = b[lasSortField];
      
      if (['pledged_value', 'eligible_loan', 'ltv_ratio'].includes(lasSortField)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (lasSortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    return filtered;
  }, [lasRecords, lasFilterBank, lasFilterMember, lasSortField, lasSortOrder]);

  const handleLASSort = (field) => {
    if (lasSortField === field) {
      setLasSortOrder(lasSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setLasSortField(field);
      setLasSortOrder('asc');
    }
  };

  const formatCurrency = (value, compact = false) => {
    if (!value && value !== 0) return '₹0';
    const num = parseFloat(value);
    if (compact) {
      if (Math.abs(num) >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
      if (Math.abs(num) >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    }
    return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSaveLAS = async (record) => {
    try {
      await api.put(`/debt/las-accounts/${record.id}`, record);
      setEditingLAS(null);
      fetchData();
      fetchLASRecords();
    } catch (error) {
      console.error('Error saving LAS record:', error);
    }
  };

  const handleSaveMTF = async (record) => {
    try {
      await api.put(`/debt/mtf-status/${record.id}`, record);
      setEditingMTF(null);
      fetchData();
    } catch (error) {
      console.error('Error saving MTF record:', error);
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'critical': return 'bg-red-50 border-red-200 text-red-800';
      case 'warning': return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'suggestion': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'info': return 'bg-green-50 border-green-200 text-green-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getInsightIconColor = (type) => {
    switch (type) {
      case 'critical': return 'text-red-500';
      case 'warning': return 'text-amber-500';
      case 'suggestion': return 'text-blue-500';
      case 'info': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin h-8 w-8 text-primary" />
          <span className="ml-2">Loading debt data...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Debt Insights</h1>
        <button onClick={fetchData} className="btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto text-sm">
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Critical Alerts - Always Visible */}
      {debtSummary?.alerts?.filter(a => a.severity === 'critical').length > 0 && (
        <div className="space-y-2">
          {debtSummary.alerts.filter(a => a.severity === 'critical').map((alert, idx) => (
            !dismissedAlerts.includes(idx) && (
              <div key={idx} className="p-3 sm:p-4 rounded-lg flex items-start sm:items-center justify-between gap-2 bg-red-100 text-red-800 border border-red-300">
                <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <AlertTriangle size={18} className="flex-shrink-0 mt-0.5 sm:mt-0" />
                  <span className="text-xs sm:text-sm break-words">{alert.message}</span>
                </div>
                <button 
                  onClick={() => setDismissedAlerts(prev => [...prev, idx])}
                  className="p-1 hover:bg-black/10 rounded-full transition-colors flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
            )
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 h-auto">
          <TabsTrigger value="dashboard" className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5">
            <Wallet size={16} className="mr-1.5 hidden sm:inline" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="las" className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5">
            <Building2 size={16} className="mr-1.5 hidden sm:inline" />
            LAS
          </TabsTrigger>
          <TabsTrigger value="mtf" className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5">
            <TrendingUp size={16} className="mr-1.5 hidden sm:inline" />
            MTF
          </TabsTrigger>
          <TabsTrigger value="payments" className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5" data-testid="tab-payments">
            <Receipt size={16} className="mr-1.5 hidden sm:inline" />
            Loan Repayment
          </TabsTrigger>
          <TabsTrigger value="pledge-flow" className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5" data-testid="tab-pledge-flow">
            <Lock size={16} className="mr-1.5 hidden sm:inline" />
            Pledge Flow
          </TabsTrigger>
          <TabsTrigger value="daily-update" className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5" data-testid="tab-daily-update">
            <Zap size={16} className="mr-1.5 hidden sm:inline" />
            Loan Status
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4 sm:space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('las')}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Total Debt</p>
                    <p className="text-lg sm:text-2xl font-bold text-red-600">
                      {formatCurrency(debtSummary?.total_debt, true)}
                    </p>
                  </div>
                  <IndianRupee className="h-6 w-6 sm:h-8 sm:w-8 text-red-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('las')}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 truncate">LAS Pledged</p>
                    <p className="text-lg sm:text-2xl font-bold text-blue-600">
                      {formatCurrency(debtSummary?.las_summary?.total_pledged, true)}
                    </p>
                    <p className="text-xs text-gray-400">
                      Eligible: {formatCurrency(debtSummary?.las_summary?.total_eligible, true)}
                    </p>
                  </div>
                  <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('las')}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Loan Used</p>
                    <p className="text-lg sm:text-2xl font-bold text-amber-600">
                      {formatCurrency(debtSummary?.bajaj_loans?.total_used, true)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {debtSummary?.bajaj_loans?.utilization_percent?.toFixed(1)}% utilized
                    </p>
                  </div>
                  <Percent className="h-6 w-6 sm:h-8 sm:w-8 text-amber-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('mtf')}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 truncate">MTF Loan</p>
                    <p className="text-lg sm:text-2xl font-bold text-purple-600">
                      {formatCurrency(debtSummary?.total_mtf_loans, true)}
                    </p>
                    <p className="text-xs text-gray-400">
                      Securities: {formatCurrency(debtSummary?.mtf_summary?.total_securities, true)}
                    </p>
                  </div>
                  <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-purple-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actionable Insights */}
          {insights.length > 0 && (
            <Card>
              <CardHeader className="cursor-pointer pb-2" onClick={() => toggleSection('insights')}>
                <CardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center gap-2">
                    <Lightbulb size={20} className="text-amber-500" />
                    <span>Actionable Insights</span>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {insights.length}
                    </span>
                  </div>
                  {expandedSections.insights ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CardTitle>
              </CardHeader>
              {expandedSections.insights && (
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {insights.map((insight, idx) => {
                      const Icon = insight.icon;
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 sm:p-4 rounded-lg border ${getInsightColor(insight.type)} flex flex-col sm:flex-row sm:items-center justify-between gap-3`}
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <Icon size={20} className={`flex-shrink-0 mt-0.5 ${getInsightIconColor(insight.type)}`} />
                            <div>
                              <p className="font-semibold text-sm">{insight.title}</p>
                              <p className="text-xs sm:text-sm opacity-90 mt-0.5">{insight.message}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setActiveTab(insight.actionTab)}
                            className="flex items-center gap-1 text-xs sm:text-sm font-medium px-3 py-1.5 rounded-md bg-white/50 hover:bg-white transition-colors self-end sm:self-auto"
                          >
                            {insight.action}
                            <ArrowRight size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* LAS by Bank Summary */}
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => toggleSection('lasByBank')}>
              <CardTitle className="flex items-center justify-between text-base sm:text-lg">
                <span>LAS by Bank</span>
                {expandedSections.lasByBank ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </CardTitle>
            </CardHeader>
            {expandedSections.lasByBank && (
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {debtSummary?.las_summary?.by_bank && Object.entries(debtSummary.las_summary.by_bank).map(([bank, data]) => (
                    <div 
                      key={bank} 
                      className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => { setLasFilterBank(bank); setActiveTab('las'); }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-700">{bank}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          bank === 'HDFC' ? 'bg-blue-100 text-blue-700' :
                          bank === 'Bajaj' ? 'bg-amber-100 text-amber-700' :
                          bank === 'IDBI' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {data.members?.length || 0} members
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Pledged:</span>
                          <span className="font-medium">{formatCurrency(data.pledged_value, true)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Eligible (50%):</span>
                          <span className="font-medium text-green-600">{formatCurrency(data.eligible_loan, true)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Reconciliation Section */}
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => toggleSection('reconciliation')}>
              <CardTitle className="flex items-center justify-between text-base sm:text-lg">
                <div className="flex items-center gap-2">
                  <span>Bank Reconciliation</span>
                  {reconciliation?.accounts_with_discrepancy > 0 && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                      {reconciliation.accounts_with_discrepancy} discrepancy
                    </span>
                  )}
                </div>
                {expandedSections.reconciliation ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </CardTitle>
            </CardHeader>
            {expandedSections.reconciliation && (
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">Account</th>
                        <th className="text-right p-2">Our Data</th>
                        <th className="text-right p-2">Bank Data</th>
                        <th className="text-right p-2">Diff</th>
                        <th className="text-center p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconciliation?.reconciliation?.map((item) => (
                        <tr key={item.account_code} className={`border-b hover:bg-gray-50 ${item.has_discrepancy ? 'bg-amber-50' : ''}`}>
                          <td className="p-2 font-medium">{item.account_code}</td>
                          <td className="p-2 text-right">{formatCurrency(item.our_available, true)}</td>
                          <td className="p-2 text-right">{formatCurrency(item.banker_available, true)}</td>
                          <td className={`p-2 text-right font-medium ${item.discrepancy !== 0 ? (item.discrepancy > 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                            {item.discrepancy !== 0 ? (item.discrepancy > 0 ? '+' : '') + formatCurrency(item.discrepancy, true) : '-'}
                          </td>
                          <td className="p-2 text-center">
                            {item.has_discrepancy ? (
                              <XCircle className="inline text-amber-500" size={16} />
                            ) : (
                              <CheckCircle className="inline text-green-500" size={16} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 font-semibold bg-gray-100">
                        <td className="p-2">TOTAL</td>
                        <td className="p-2 text-right">{formatCurrency(reconciliation?.total_our_available, true)}</td>
                        <td className="p-2 text-right">{formatCurrency(reconciliation?.total_banker_available, true)}</td>
                        <td className={`p-2 text-right ${reconciliation?.total_discrepancy !== 0 ? (reconciliation?.total_discrepancy > 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                          {reconciliation?.total_discrepancy !== 0 ? (reconciliation?.total_discrepancy > 0 ? '+' : '') + formatCurrency(reconciliation?.total_discrepancy, true) : '-'}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* LAS Tab - Includes all LAS data including Bajaj LAS */}
        <TabsContent value="las" className="space-y-4">
          {/* LAS Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">Total Pledged</p>
                <p className="text-lg sm:text-xl font-bold text-blue-600">
                  {formatCurrency(debtSummary?.las_summary?.total_pledged, true)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">Eligible Loan</p>
                <p className="text-lg sm:text-xl font-bold text-green-600">
                  {formatCurrency(debtSummary?.las_summary?.total_eligible, true)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">Loan Used</p>
                <p className="text-lg sm:text-xl font-bold text-amber-600">
                  {formatCurrency(debtSummary?.bajaj_loans?.total_used, true)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">Available Credit</p>
                <p className="text-lg sm:text-xl font-bold text-purple-600">
                  {formatCurrency(
                    (debtSummary?.bajaj_loans?.total_limit || 0) - (debtSummary?.bajaj_loans?.total_used || 0), 
                    true
                  )}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Bajaj Loan Accounts Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Loan Accounts (Bajaj)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2">Account</th>
                      <th className="text-right p-2">Used</th>
                      <th className="text-right p-2">Available</th>
                      <th className="text-right p-2">Limit</th>
                      <th className="text-right p-2">LTV %</th>
                      <th className="text-right p-2">Deficit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debtSummary?.bajaj_loans?.accounts?.map((acc) => (
                      <tr key={acc.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{acc.account_code}</td>
                        <td className="p-2 text-right">{formatCurrency(acc.amount_used, true)}</td>
                        <td className={`p-2 text-right ${acc.available < 0 ? 'text-red-600 font-semibold' : 'text-green-600'}`}>
                          {formatCurrency(acc.available, true)}
                        </td>
                        <td className="p-2 text-right">{formatCurrency(acc.sanction_limit, true)}</td>
                        <td className={`p-2 text-right ${acc.ltv_percent > 50 ? 'text-red-600 font-semibold' : ''}`}>
                          {acc.ltv_percent?.toFixed(1)}%
                        </td>
                        <td className={`p-2 text-right ${acc.deficit < 0 ? 'text-red-600' : ''}`}>
                          {formatCurrency(acc.deficit, true)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* LAS Pledged Holdings by Member */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-base sm:text-lg">
                <span>LAS Holdings by Member</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Bank</label>
                  <select
                    value={lasFilterBank}
                    onChange={(e) => setLasFilterBank(e.target.value)}
                    className="input-field text-sm py-1.5"
                  >
                    <option value="all">All Banks</option>
                    {uniqueBanks.map(bank => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Member</label>
                  <select
                    value={lasFilterMember}
                    onChange={(e) => setLasFilterMember(e.target.value)}
                    className="input-field text-sm py-1.5"
                  >
                    <option value="all">All Members</option>
                    {uniqueMembers.map(member => (
                      <option key={member} value={member}>{member}</option>
                    ))}
                  </select>
                </div>
                {(lasFilterBank !== 'all' || lasFilterMember !== 'all') && (
                  <button
                    onClick={() => { setLasFilterBank('all'); setLasFilterMember('all'); }}
                    className="text-sm text-primary hover:underline self-end pb-1"
                  >
                    Clear Filters
                  </button>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th 
                        className="text-left p-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleLASSort('member_name')}
                      >
                        <div className="flex items-center gap-1">
                          Member
                          <ArrowUpDown size={14} className={lasSortField === 'member_name' ? 'text-primary' : 'text-gray-400'} />
                        </div>
                      </th>
                      <th 
                        className="text-left p-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleLASSort('bank_name')}
                      >
                        <div className="flex items-center gap-1">
                          Bank
                          <ArrowUpDown size={14} className={lasSortField === 'bank_name' ? 'text-primary' : 'text-gray-400'} />
                        </div>
                      </th>
                      <th 
                        className="text-right p-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleLASSort('pledged_value')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Pledged
                          <ArrowUpDown size={14} className={lasSortField === 'pledged_value' ? 'text-primary' : 'text-gray-400'} />
                        </div>
                      </th>
                      <th 
                        className="text-right p-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleLASSort('eligible_loan')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Eligible
                          <ArrowUpDown size={14} className={lasSortField === 'eligible_loan' ? 'text-primary' : 'text-gray-400'} />
                        </div>
                      </th>
                      <th className="text-right p-2">LTV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSortedLASRecords.map((record) => (
                      <tr key={record.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{record.member_name}</td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            record.bank_name === 'HDFC' ? 'bg-blue-100 text-blue-700' :
                            record.bank_name === 'Bajaj' ? 'bg-amber-100 text-amber-700' :
                            record.bank_name === 'IDBI' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {record.bank_name}
                          </span>
                        </td>
                        <td className="p-2 text-right">{formatCurrency(record.pledged_value, true)}</td>
                        <td className="p-2 text-right">{formatCurrency(record.eligible_loan, true)}</td>
                        <td className="p-2 text-right">{((record.ltv_ratio || 0.5) * 100).toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 font-semibold bg-gray-100">
                      <td className="p-2" colSpan={2}>
                        TOTAL {lasFilterBank !== 'all' || lasFilterMember !== 'all' ? '(Filtered)' : ''}
                      </td>
                      <td className="p-2 text-right">{formatCurrency(filteredSortedLASRecords.reduce((sum, r) => sum + (r.pledged_value || 0), 0), true)}</td>
                      <td className="p-2 text-right">{formatCurrency(filteredSortedLASRecords.reduce((sum, r) => sum + (r.eligible_loan || 0), 0), true)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MTF Tab - Includes all MTF data including Bajaj MTF */}
        <TabsContent value="mtf" className="space-y-4">
          {/* MTF Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">MTF Loan</p>
                <p className="text-lg sm:text-xl font-bold text-purple-600">
                  {formatCurrency(debtSummary?.total_mtf_loans, true)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">MTF Portfolio Value</p>
                <p className="text-lg sm:text-xl font-bold text-blue-600">
                  {formatCurrency(debtSummary?.mtf_summary?.total_funding_stock, true)}
                </p>
                <p className="text-xs text-gray-400">
                  Margin paid: {formatCurrency(debtSummary?.mtf_summary?.total_securities, true)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">Margin Available</p>
                {(() => {
                  // Margin Available = Current MTF Portfolio Value - Net MTF Loan
                  const portfolioValue = debtSummary?.mtf_summary?.total_funding_stock || 0;
                  const netMtfLoan = debtSummary?.total_mtf_loans || 0;
                  const marginAvailable = portfolioValue - netMtfLoan;
                  const isPositive = marginAvailable >= 0;
                  return (
                    <>
                      <p className={`text-lg sm:text-xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                        {isPositive ? '+' : ''}{formatCurrency(marginAvailable, true)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {isPositive ? 'Credit for new purchase' : 'Funds required'}
                      </p>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">Avg Safety Cover</p>
                <p className={`text-lg sm:text-xl font-bold ${
                  (debtSummary?.mtf_summary?.accounts?.reduce((sum, acc) => sum + (acc.safety_cover_percent || 0), 0) / 
                  (debtSummary?.mtf_summary?.accounts?.length || 1)) < 15 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {(debtSummary?.mtf_summary?.accounts?.reduce((sum, acc) => sum + (acc.safety_cover_percent || 0), 0) / 
                    (debtSummary?.mtf_summary?.accounts?.length || 1)).toFixed(1)}%
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">Total Limit</p>
                <p className="text-lg sm:text-xl font-bold text-gray-600">
                  {formatCurrency(
                    debtSummary?.mtf_summary?.accounts?.reduce((sum, acc) => sum + (acc.limit || 0), 0), 
                    true
                  )}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* MTF Accounts Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">MTF Account Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2">Account</th>
                      <th className="text-right p-2">Safety Cover</th>
                      <th className="text-right p-2">Securities</th>
                      <th className="text-right p-2">Funding</th>
                      <th className="text-right p-2">Outstanding</th>
                      <th className="text-right p-2">Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debtSummary?.mtf_summary?.accounts?.map((acc) => {
                      const isEditing = editingMTF?.id === acc.id;
                      
                      return (
                        <tr key={acc.id} className="border-b hover:bg-gray-50">
                          <td className="p-2 font-medium">{acc.account_code}</td>
                          <td className={`p-2 text-right ${acc.safety_cover_percent < 15 ? 'text-red-600 font-semibold' : 'text-green-600'}`}>
                            {isEditing ? (
                              <input
                                type="number"
                                step="0.01"
                                className="input-field w-20 text-right text-sm"
                                value={editingMTF.safety_cover_percent}
                                onChange={(e) => setEditingMTF({...editingMTF, safety_cover_percent: parseFloat(e.target.value) || 0})}
                              />
                            ) : `${acc.safety_cover_percent?.toFixed(1)}%`}
                          </td>
                          <td className="p-2 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                className="input-field w-24 text-right text-sm"
                                value={editingMTF.securities_value}
                                onChange={(e) => setEditingMTF({...editingMTF, securities_value: parseFloat(e.target.value) || 0})}
                              />
                            ) : formatCurrency(acc.securities_value, true)}
                          </td>
                          <td className="p-2 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                className="input-field w-24 text-right text-sm"
                                value={editingMTF.funding_stock_value}
                                onChange={(e) => setEditingMTF({...editingMTF, funding_stock_value: parseFloat(e.target.value) || 0})}
                              />
                            ) : formatCurrency(acc.funding_stock_value, true)}
                          </td>
                          <td className="p-2 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                className="input-field w-24 text-right text-sm"
                                value={editingMTF.outstanding}
                                onChange={(e) => setEditingMTF({...editingMTF, outstanding: parseFloat(e.target.value) || 0})}
                              />
                            ) : formatCurrency(acc.outstanding, true)}
                          </td>
                          <td className="p-2 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                className="input-field w-24 text-right text-sm"
                                value={editingMTF.limit}
                                onChange={(e) => setEditingMTF({...editingMTF, limit: parseFloat(e.target.value) || 0})}
                              />
                            ) : formatCurrency(acc.limit, true)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* MTF Risk Indicators */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Risk Indicators</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {debtSummary?.mtf_summary?.accounts?.map((acc) => {
                  const riskLevel = acc.safety_cover_percent < 10 ? 'critical' : 
                                   acc.safety_cover_percent < 15 ? 'warning' : 'safe';
                  return (
                    <div 
                      key={acc.id} 
                      className={`p-4 rounded-lg border ${
                        riskLevel === 'critical' ? 'bg-red-50 border-red-200' :
                        riskLevel === 'warning' ? 'bg-amber-50 border-amber-200' :
                        'bg-green-50 border-green-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-700">{acc.account_code}</span>
                        <span className={`text-lg font-bold ${
                          riskLevel === 'critical' ? 'text-red-600' :
                          riskLevel === 'warning' ? 'text-amber-600' :
                          'text-green-600'
                        }`}>
                          {acc.safety_cover_percent?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            riskLevel === 'critical' ? 'bg-red-500' :
                            riskLevel === 'warning' ? 'bg-amber-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(acc.safety_cover_percent || 0, 100)}%` }}
                        />
                      </div>
                      <p className={`text-xs mt-2 ${
                        riskLevel === 'critical' ? 'text-red-700' :
                        riskLevel === 'warning' ? 'text-amber-700' :
                        'text-green-700'
                      }`}>
                        {riskLevel === 'critical' ? 'Margin call risk - Add funds immediately' :
                         riskLevel === 'warning' ? 'Below safe threshold - Monitor closely' :
                         'Healthy safety margin'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab (Loan Payments Ledger) */}
        <TabsContent value="payments" className="space-y-4" data-testid="payments-tab-content">
          <LoanPayments user={user} setUser={setUser} embedded />
        </TabsContent>

        {/* Pledge Flow Tab */}
        <TabsContent value="pledge-flow" className="space-y-4" data-testid="pledge-flow-tab-content">
          <PledgeFlow user={user} setUser={setUser} embedded />
        </TabsContent>

        {/* Daily Update Tab (Debt Daily Update) */}
        <TabsContent value="daily-update" className="space-y-4" data-testid="daily-update-tab-content">
          <BajajUpdate user={user} setUser={setUser} embedded />
        </TabsContent>
      </Tabs>
    </div>
    </Layout>
  );
};

export default DebtManagement;
